import { z } from 'zod';
import { RESERVED_PAGE_SLUGS } from './website-slugs';

export const REDIRECT_LIMIT = 200;

const FROM_PATH = /^\/[^\s?#\\]*$/;
const FROM_HASH = /^#\/[^\s\\]+$/;
const TO_PATH = /^\/(?![/\\])[^\s\\]*$/;

function isHttps(value: string): boolean {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

export const redirectSchema = z.object({
  from: z
    .string()
    .trim()
    .max(300)
    .refine((from) => FROM_PATH.test(from) || FROM_HASH.test(from), 'A path such as /gallery, or a hash route such as #/product'),
  to: z
    .string()
    .trim()
    .max(500)
    .refine((to) => TO_PATH.test(to) || isHttps(to), 'A path on the site, or an https address'),
  permanent: z.boolean().default(true).describe('301 when true, 302 when false'),
});
export type SiteRedirect = z.infer<typeof redirectSchema>;

/** Addresses the site or the app answer themselves, so a redirect from them would never be reached or would take them away. */
const RESERVED_SEGMENTS = new Set<string>([...RESERVED_PAGE_SLUGS, 'careers', 'assets', 'invite', 'member-link', 'receipts', 'pay', 'robots.txt', 'sitemap.xml']);

/** The form two addresses are compared in: decoded, lower case, without a trailing slash. */
function addressKey(value: string): string {
  let decoded = value;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    // A malformed escape is compared as written.
  }
  const lower = decoded.toLowerCase();
  return lower.length > 1 && lower.endsWith('/') ? lower.replace(/\/+$/, '') || '/' : lower;
}

/** Whether a redirect may start at this path at all: never at the home page or an address the site answers itself. */
export function redirectablePath(pathname: string): boolean {
  const segment = addressKey(pathname).split('/')[1] ?? '';
  return segment !== '' && !RESERVED_SEGMENTS.has(segment);
}

/** Where a redirect's target lands, as a `from` would be written: its hash route when it opens one on the home page, otherwise its path. */
function targetKey(to: string): string | null {
  if (!to.startsWith('/')) return null;
  if (to.startsWith('/#/')) return addressKey(to.slice(1));
  return addressKey(to.split(/[?#]/)[0]!);
}

export type RedirectProblemCode = 'REDIRECT_DUPLICATE' | 'REDIRECT_RESERVED' | 'REDIRECT_PAGE' | 'REDIRECT_LOOP';

export interface RedirectProblem {
  index: number;
  from: string;
  code: RedirectProblemCode;
}

/**
 * What is wrong with a list of redirects as a whole: a `from` listed twice, one the site answers itself, one
 * a page of the site already has, or redirects that lead back to where they started.
 */
export function redirectProblems(redirects: readonly SiteRedirect[], pageSlugs: readonly string[]): RedirectProblem[] {
  const pages = new Set(pageSlugs.map((slug) => `/${slug.toLowerCase()}`));
  const byFrom = new Map<string, SiteRedirect>();
  const problems: RedirectProblem[] = [];
  redirects.forEach((redirect, index) => {
    const from = addressKey(redirect.from);
    if (byFrom.has(from)) problems.push({ index, from: redirect.from, code: 'REDIRECT_DUPLICATE' });
    else if (from.startsWith('/') && !redirectablePath(from)) problems.push({ index, from: redirect.from, code: 'REDIRECT_RESERVED' });
    else if (pages.has(from)) problems.push({ index, from: redirect.from, code: 'REDIRECT_PAGE' });
    else byFrom.set(from, redirect);
  });
  redirects.forEach((redirect, index) => {
    const start = addressKey(redirect.from);
    if (byFrom.get(start) !== redirect) return;
    const seen = new Set([start]);
    let next = targetKey(redirect.to);
    while (next !== null && byFrom.has(next)) {
      if (seen.has(next)) {
        problems.push({ index, from: redirect.from, code: 'REDIRECT_LOOP' });
        return;
      }
      seen.add(next);
      next = targetKey(byFrom.get(next)!.to);
    }
  });
  return problems.sort((a, b) => a.index - b.index);
}

/**
 * The redirect for an address the visitor opened: by path, or by the `#/…` hash an old single-page site used.
 * A page of the site at the same address always wins, even when it was added after the redirect.
 */
export function findRedirect(redirects: readonly SiteRedirect[], at: { pathname?: string; hash?: string }, pageSlugs: readonly string[] = []): SiteRedirect | null {
  const keys = [at.hash && at.hash.startsWith('#/') ? addressKey(at.hash) : null, at.pathname ? addressKey(at.pathname) : null].filter((key): key is string => key !== null);
  if (keys.length === 0) return null;
  const pages = new Set(pageSlugs.map((slug) => `/${slug.toLowerCase()}`));
  return redirects.find((redirect) => {
    const from = addressKey(redirect.from);
    return keys.includes(from) && !pages.has(from);
  }) ?? null;
}
